import React from 'react'
import { Loader } from '@calendly/ui/components/loader'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import { Settings } from 'services/settings'

class PayButton extends React.Component {
  state = {
    loading: true
  }

  wrapperRef = React.createRef()
  buttonsInstance = null
  mounted = false

  componentDidMount() {
    this.mounted = true
    this.mountButtons()
  }

  componentDidUpdate(prevProps) {
    // If inputs change, re-mount PayPal buttons to avoid duplicated/stacked iframes.
    const relevantChanged =
      prevProps.locale !== this.props.locale ||
      prevProps.commit !== this.props.commit ||
      prevProps.paypalEnv !== this.props.paypalEnv ||
      prevProps.paypalClientId !== this.props.paypalClientId ||
      prevProps.isPpcp !== this.props.isPpcp

    if (relevantChanged) this.mountButtons()
  }

  componentWillUnmount() {
    this.mounted = false
    // PayPal SDK injects iframes/scripts into the container; ensure they don't linger across remounts,
    // which can lead to duplicated button UIs when the payment form is re-rendered.
    if (this.wrapperRef.current) this.wrapperRef.current.innerHTML = ''
    this.buttonsInstance = null
  }

  mountButtons() {
    const container = this.wrapperRef.current
    if (!container) return
    if (!window.paypal?.Buttons) return

    container.innerHTML = ''
    this.buttonsInstance = null
    if (this.mounted) this.setState({ loading: true })

    const paypalEnv = this.props.paypalEnv ?? Settings.paypal.env
    const paypalClientId = this.props.paypalClientId ?? Settings.paypal.client_id

    const buttons = window.paypal.Buttons({
      style: PayButton.getButtonStyle({ isPpcp: this.props.isPpcp }),
      env: paypalEnv,
      client: { [paypalEnv]: paypalClientId },
      locale: this.props.locale,
      commit: this.props.commit,
      createOrder: (data, actions) => this.props.onCreateOrder(data, actions),
      onApprove: (data, actions) => this.props.onApprove(data, actions),
      onError: (data, actions) => this.props.onError(data, actions),
    })

    this.buttonsInstance = buttons

    // render() returns a promise; once resolved, the iframe/buttons are mounted.
    buttons.render(container).then(() => {
      if (this.mounted) this.setState({ loading: false })
    }).catch(() => {
      if (this.mounted) this.setState({ loading: false })
    })
  }

  render() {
    return (
      <>
        <div
          ref={this.wrapperRef}
          className={this.getWrapperClassName()}
          style={this.getWrapperStyle()}
        />
        {this.state.loading ? <Loader /> : undefined}
      </>
    )
  }

  getWrapperClassName() {
    return classNames(
      'paypal-frame-wrapper',
      this.props.className,
    )
  }

  getWrapperStyle() {
    if (this.state.loading) {
      return { display: 'none' }
    }
    return { display: 'block' }
  }

  static getButtonStyle({ isPpcp } = {}) {
    return {
      layout: isPpcp ? 'vertical' : 'horizontal',
      color: 'gold',
      shape: 'rect',
      label: 'pay',
      tagline: false,
    }
  }
}

PayButton.propTypes = {
  onCreateOrder: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  commit: PropTypes.bool,
  locale: PropTypes.string,
  onError: PropTypes.func,
  className: PropTypes.string,
  paypalEnv: PropTypes.string,
  paypalClientId: PropTypes.string,
  isPpcp: PropTypes.bool
}

PayButton.defaultProps = {
  commit: false,
  locale: 'en_US',
  onError: () => undefined,
  className: undefined
}

export default PayButton
